/* eslint-disable camelcase */
import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { cioFacetsToUrlFilters } from '~/grid/utils/cioUtils';
import { slugify } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useProductGridData from '../../hooks/useProductGridData';
import useSelectedFacetsToApply from '../../hooks/useSelectedFacetsToApply';
import { isCustomBraFilter, sanitizeFilterName } from '../../utils/filters';

import MobileGridCustomBraFilter from './MobileGridCustomBraFilter';
import MobileGridGroupedFilterButtonInput from './MobileGridGroupedFilterButtonInput';

const DynamicFormattedMessage = FormattedMessage;

const GroupListContainer = styled.ul`
  display: flex;
  flex-direction: column;
  padding-left: 8px;
  list-style: none;
  gap: 32px;
`;

const GroupListItem = styled.li``;

const SubFacetTitle = styled.h3`
  display: flex;
  padding-bottom: 8px;
  ${({ theme }) => theme.gridFilterStyles.nestedHeadingStyle}
  font-size: 14px;
`;

const CheckboxGroup = styled.fieldset`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  justify-content: space-between;
  border: none;
`;

const HiddenLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const MobileGridGroupedFilter = ({ sub_facets = [], name }) => {
  const { selectedUrlFilters } = useProductGridData();
  const { toggleSelectedFacetsToApply, isFacetOptionSelected } =
    useSelectedFacetsToApply();

  const handleOnChange = ({ facetName, facetOption }) => {
    toggleSelectedFacetsToApply({
      facetName,
      facetOption,
    });
  };

  if (!sub_facets?.length) {
    return null;
  }

  return (
    <GroupListContainer>
      {sub_facets.map((subFacet) => {
        if (isCustomBraFilter(subFacet.name)) {
          return (
            <MobileGridCustomBraFilter
              key={subFacet.name}
              name={subFacet.name}
              display_name={subFacet.display_name}
              options={subFacet.options}
            />
          );
        }

        return (
          <GroupListItem key={subFacet.name}>
            <SubFacetTitle>
              <DynamicFormattedMessage
                id={`site_product_filter.${slugify(
                  cioFacetsToUrlFilters[subFacet.name] || subFacet.name
                )}`}
                defaultMessage={subFacet.display_name}
              />
            </SubFacetTitle>
            <CheckboxGroup>
              <HiddenLegend>{subFacet.display_name}</HiddenLegend>
              {subFacet.options?.map((option) => {
                const inputId = `${subFacet.name}-${option.value}`;
                const isSelected = isFacetOptionSelected({
                  facetName: subFacet.name,
                  facetOption: option.value,
                  selectedUrlFilters,
                });

                return (
                  <MobileGridGroupedFilterButtonInput
                    key={option.value}
                    id={inputId}
                    name={option.value}
                    label={option.display_name}
                    checked={isSelected}
                    onChange={() =>
                      handleOnChange({
                        facetName: subFacet.name,
                        facetOption: option.value,
                      })
                    }
                    filterName={sanitizeFilterName(subFacet.name)}
                  />
                );
              })}
            </CheckboxGroup>
          </GroupListItem>
        );
      })}
    </GroupListContainer>
  );
};

MobileGridGroupedFilter.propTypes = {
  name: PropTypes.string.isRequired,
  sub_facets: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          display_name: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ),
};

export default MobileGridGroupedFilter;
